/**
 * Creates a ResizeObserver that batches callbacks using requestAnimationFrame.
 *
 * When layout changes trigger multiple ResizeObserver callbacks in quick succession
 * (e.g. during LLM streaming or rapid DOM updates), processing each callback
 * immediately causes cascading layout recalculations and long tasks (835ms+).
 *
 * Batching via rAF coalesces multiple resize events into a single callback per frame,
 * reducing main-thread blocking and improving INP.
 *
 * The batched callback receives **at most one entry per observed element**: the last
 * size the browser reported for it during the frame. A single-element observer can
 * therefore destructure `([entry])` and get the settled size, never a stale intermediate
 * one. An observer watching several elements must match on `entry.target`, because a
 * frame carries only the elements that actually resized in it, in delivery order.
 *
 * @see T5 — Debounce ResizeObserver (https://www.notion.so/heidihealth/scribe-fe-perf-summary-tickets-325ca630286e80a88f53ce44a52f9d7d?source=copy_link)
 */
export function createResizeObserverWithRAF(
  callback: ResizeObserverCallback
): ResizeObserver {
  let rafId: number | null = null;
  // Keyed by target so several deliveries for one element within a frame collapse to its
  // settled size. Cleared every frame, so a frame holds only the elements that resized.
  const pendingEntries = new Map<Element, ResizeObserverEntry>();
  let pendingObserver: ResizeObserver | null = null;

  const processEntries = () => {
    rafId = null;
    if (pendingObserver) {
      const entries = [...pendingEntries.values()];
      const observer = pendingObserver;
      pendingEntries.clear();
      pendingObserver = null;
      callback(entries, observer);
    }
  };

  const nativeObserver = new ResizeObserver(
    (entries: ResizeObserverEntry[], obs: ResizeObserver) => {
      for (const entry of entries) {
        pendingEntries.set(entry.target, entry);
      }
      pendingObserver = obs;
      if (rafId === null) {
        rafId = requestAnimationFrame(processEntries);
      }
    }
  );

  return {
    observe: (target: Element, options?: ResizeObserverOptions) =>
      nativeObserver.observe(target, options),
    unobserve: (target: Element) => {
      // Drop any size already queued for this target, so a pending frame cannot deliver
      // an entry for an element the caller has stopped observing.
      pendingEntries.delete(target);
      nativeObserver.unobserve(target);
    },
    disconnect: () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
        rafId = null;
      }
      pendingEntries.clear();
      pendingObserver = null;
      nativeObserver.disconnect();
    },
  } as ResizeObserver;
}
